import { useMemo } from 'react'
import type { CustomDataArtifact } from '@dev-streetbeat/chat/types'

import { BriefChart, type BriefChartSpec, parseChart } from './BriefCharts'
import styles from './AllocationChartArtifact.module.scss'

// chat-ui's ArtifactProps type isn't re-exported from the package root, so
// redeclare the minimal runtime contract (mirrors ClientBriefArtifact).
interface ArtifactProps<T> {
  artifact: T
  onLoaded?: () => void
}

interface AllocationChartPayload {
  client_label: string
  chart: BriefChartSpec | null
  source_summary: string
}

function parsePayload(data: unknown): AllocationChartPayload | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>
  return {
    client_label: typeof raw.client_label === 'string' ? raw.client_label.trim() : '',
    chart: parseChart(raw.chart),
    source_summary: typeof raw.source_summary === 'string' ? raw.source_summary.trim() : '',
  }
}

/**
 * Renderer for the credem.allocation_chart artifact.
 *
 * Reuses the exact donut the client brief shows (BriefChart -> BriefDonut)
 * so the in-chat chart is visually identical to the brief's composition
 * chart. The backend reuses the brief's chart builder, so the payload's
 * `chart` is the same spec a `donut` section carries.
 */
export function AllocationChartArtifact({ artifact }: ArtifactProps<CustomDataArtifact>) {
  const payload = useMemo(() => parsePayload(artifact.custom_data?.data), [artifact])
  if (!payload) return null
  return (
    <section className={styles.card}>
      {payload.client_label ? (
        <header className={styles.header}>{payload.client_label}</header>
      ) : null}
      {payload.chart ? (
        <BriefChart chart={payload.chart} />
      ) : (
        <p className={styles.empty}>
          Nessuna allocazione investita da rappresentare per questo cliente.
        </p>
      )}
      {payload.source_summary ? (
        <p className={styles.source}>{payload.source_summary}</p>
      ) : null}
    </section>
  )
}
